import AppSettings from '../AppSettings';
import * as api from '../net/api';
import CanvasObject from './CanvasObject';
import { CANVAS_ACTIONS } from './canvasActionTypes';
import { roundRect, allInside } from './canvas-helper';
import RedCros from '../../img/red-cross.png';
import '../../core/lib/canvasToBlob';
import { TEXT } from './itemTypes';

function convertFont(fontStyle = {}) {
	const { height, name } = fontStyle;
	let font = `${height}px ${name}`;
	if (fontStyle.bold) {
		font = 'bold ' + font;
	}
	if (fontStyle.italic) {
		font = 'italic ' + font;
	}

	return font;
}

function addDebugMarker(ctx,x,y,colour) {

	if(!AppSettings.isLocal()) return;

	ctx.save();
	ctx.strokeStyle = colour;
	roundRect(ctx, x - 5, y - 5, 10, 10, 1);
	ctx.restore();
}

class TextItem extends CanvasObject {

	constructor (id, text, ctx, options = {}) {
		super();

		this.ctx = ctx;

		this.bleeding = options.bleeding || 3;
		this.baseLine = 10;

		this.coverageArea = options.coverageArea;
		this.templateArea = options.allowedArea;
		this.layerType = 'CustomImageLayer';
		this.canvasScale = options.canvasScale;

		//allow for portrait mode -----------------------
		this.coverageArea = {
			x: options.coverageArea.x,
			y: options.coverageArea.y,
			width: (options.isPortrait)? options.coverageArea.height : options.coverageArea.width,
			height: (options.isPortrait)? options.coverageArea.width : options.coverageArea.height
		}

		this.width = this.coverageArea.width; 
		this.height = this.coverageArea.height;


		this.type = TEXT;

		this.scaledSize = Math.floor(25 * this.canvasScale); //25 is default

		this.fontStyle = options.fontStyle || {
			name: defaultFont, // Arial, Helvetica
			height: this.scaledSize,
			color: localStorage.getItem('colors') ? localStorage.getItem('colors') : currentColor,
			stroke: false,
			bold: false,
			italic: false,
			shadow: false
		};
			
		this.id = id;

		this.maxWidth = options.coverageArea.width;
		this.height = +this.fontStyle.height + this.baseLine;

		this.boxMargin = 5;
		this.shadowOffset = 1;

		this.selected = true;
		this.rotation = 0;

		this.setText(text);
		this.testCoverage();

		this.borderColor = options.borderColor || 'rgb(49, 183, 219)';

		this.removed = false;

		const removedIcon = new Image();
		removedIcon.src = RedCros;
		removedIcon.onload = () => {
			this.removedIcon = removedIcon;
		};

	}

	measureTextWidth (text, fontStyle, canvasContext) {

		canvasContext.font = convertFont(fontStyle);
		canvasContext.textAlign = 'left';
		return canvasContext.measureText(text).width + (this.boxMargin * 2)
	}

	setText (text) {

		this.text = text;

		this.originPoint = {
			x: 0,
			y: 0 
		};
	}

	setFontSize(font_style,size) {

		this.fontStyle = font_style; 
		this.fontStyle.height = +size;

		this.width = this.measureTextWidth(this.text, this.fontStyle, this.ctx);
	}

	setFontStyle(font_style) {

		this.fontStyle = font_style;
		
		this.fontStyle.height =  Math.floor(this.canvasScale * this.fontStyle.height); 

		this.width = this.measureTextWidth(this.text, this.fontStyle, this.ctx);
	}

	getFontStyle() {
		return Object.assign({}, this.fontStyle);
	}

	render(ctx) {

		if (!this.text) { return; }

		const { width, height } = ctx.canvas;

		const canvasCenter = {
			x: width / 2,
			y: height / 2
		};

		addDebugMarker(ctx, canvasCenter.x, canvasCenter.y, '#ff0000' );  //marks canvas actual centre

		// show item coverage area ------------------------------
		if (this.selected) {
			ctx.save();
			ctx.translate(canvasCenter.x, canvasCenter.y);
			ctx.strokeStyle = '#ff0000';
			roundRect(ctx, this.coverageArea.x, this.coverageArea.y, this.coverageArea.width, this.coverageArea.height, 2);
			ctx.restore();
		}

		ctx.save();

		ctx.translate(this.originPoint.x + canvasCenter.x, this.originPoint.y + canvasCenter.y);

		addDebugMarker(ctx, 0, 0, '#ffff00' ); //marks centre of this

		if (this.rotation !== 0) {
			ctx.rotate(this.rotation * Math.PI / 180);
		}

		if (this.flipped) {
			ctx.scale(-1, 1);
		}

		//draw text -----------------------------------------------------------
		this.textH = Math.floor(this.fontStyle.height);	
		this.height = 0;  //starting point. Is adjusted later for font & number of lines
		this.width = 0;

		//get total height for text---------
		this.textlines = this.text.split("\n");
		this.textlines.forEach(line => {
			this.height += this.textH;
		});

		this.height += (this.boxMargin * 2);

		ctx.beginPath();

		ctx.fillStyle = this.fontStyle.color;

		var textw,texth;

		var startY = this.textH - (this.height/2) + this.boxMargin; 

		ctx.textBaseline = 'bottom';

		this.textlines.forEach(line => {

			textw = this.measureTextWidth(line, this.fontStyle, this.ctx);// * this.canvasScale;
			if (textw > this.width) this.width = textw;

			if (this.fontStyle.shadow) {
				ctx.fillStyle = 'rgba( 0,0,0,.4)';
				ctx.fillText(line, -this.width / 2 + this.shadowOffset, startY + this.shadowOffset, this.width, this.maxWidth);
			} 

			ctx.fillStyle = this.fontStyle.color;
			ctx.fillText(line, -this.width/2, startY);

			startY += this.textH;
		})


		//draw handle-box------------------------------------
		if (this.selected) {
			ctx.strokeStyle = '#ff0000'; //this.borderColor;

			//? use ctx.linewidth to control stroke thickness
			//? use ctx.stokeStyle = rgba(~colour params, alpha param~) for translucent line

			roundRect(ctx, -this.width / 2, -this.height / 2 , this.width, this.height, 2);

			// show border of item
			// draw left top corner
			ctx.beginPath();
			ctx.arc(-this.width / 2, -this.height / 2, 5, 0, 2 * Math.PI);
			ctx.fillStyle = this.hoverCorners.leftTop ? this.borderColor : 'white';
			ctx.fill();
			ctx.stroke();

			// draw right top corner
			ctx.beginPath();
			ctx.arc(this.width / 2, -this.height / 2, 5, 0, 2 * Math.PI);
			ctx.fillStyle = this.hoverCorners.rightTop ? this.borderColor : 'white';
			ctx.fill();
			ctx.stroke();

			//draw left bottom corner
			ctx.beginPath();
			ctx.fillStyle = this.hoverCorners.leftBottom ? this.borderColor : 'white';
			ctx.arc(-this.width / 2, this.height / 2, 5, 0, 2 * Math.PI);
			ctx.fill();
			ctx.stroke();

			//draw right bottom corner
			ctx.beginPath();
			ctx.arc(this.width / 2, this.height / 2, 5, 0, 2 * Math.PI);
			ctx.fillStyle = this.hoverCorners.rightBottom ? this.borderColor : 'white';
			ctx.fill();
			ctx.stroke();
		}

		ctx.restore();

		if (this.removed && this.removedIcon) {

			ctx.save();

			const { x, y } = this.originPoint;
			ctx.translate(x + canvasCenter.x, y + canvasCenter.y);
			if (this.rotation !== 0) {
				ctx.rotate(this.rotation * Math.PI / 180);
			}

			ctx.beginPath();
			ctx.fillStyle = 'rgba(200,200,200, 1)';
			ctx.arc(-this.width / 2, -this.height / 2, 10, 0, 2 * Math.PI);
			ctx.fill();

			ctx.drawImage(this.removedIcon, -this.width / 2 - 5, -this.height / 2 - 5, 10, 10);

			ctx.restore();
		}
	}

	hover(pos) {
		super.hover(pos);
		if (this.action === CANVAS_ACTIONS.SCALING_ROTATION) {
			this.fontStyle.height = this.height - this.baseLine;
		}
	}

	hitTest(pos) {
		return (pos.x >= this.originPoint.x - this.width / 2
			&& pos.x <= this.originPoint.x + this.width / 2
			&& pos.y >= this.originPoint.y - this.height / 2
			&& pos.y <= this.originPoint.y + this.height / 2)
			|| (this.hoverCorners.leftTop
				|| this.hoverCorners.leftBottom
				|| this.hoverCorners.rightTop
				|| this.hoverCorners.rightBottom
				|| this.hoverCorners.rotatePoint
			);
	}

	testCoverage() {
		const vertices = [this.getLeftBottomCorner(),
		this.getRightTopCorner(),
		this.getLeftTopCorner(),
		this.getRightBottomCorner()];
		this.errorCoverage = !allInside(vertices, this.coverageArea);

		let itemMargin = 8; //account for margin between text and the handle-box;

		this.removed = 
			   this.originPoint.x + this.width / 2 < this.templateArea.x + itemMargin
			|| this.originPoint.x - this.width / 2 > this.templateArea.x + this.templateArea.width - itemMargin
			|| this.originPoint.y + this.height / 2 < this.templateArea.y + itemMargin
			|| this.originPoint.y - this.height / 2 > this.templateArea.y + this.templateArea.height - itemMargin;


		// Show info message about delete the selected items
		//const info = document.querySelector("#info-message");
		const info = document.querySelector("#feature-info-message");
		
		if (!this.removed) {
			info.classList.add('show');
		} else {
			info.classList.remove('show');
		}

		return this.errorCoverage;
	}

	handleResize(scale) {

		this.fontStyle.height *= scale;
		this.canvasScale *= scale;

		this.originPoint = {
			x: this.originPoint.x * scale,
			y: this.originPoint.y * scale
		};

		this.coverageArea.x *= scale;
		this.coverageArea.y *= scale;
		this.coverageArea.width *= scale;
		this.coverageArea.height *= scale;
	}

	preview(scale, ctx) {
		const { width, height } = ctx.canvas;

		const canvasCenter = {
			x: width / 2,
			y: height / 2
		};

		const item = {
			text: this.text,
			fontStyle: { ...this.fontStyle },
			width: 0, 
			height: 0, 
			rotation: this.rotation,
			flipped: this.flipped,
			originPoint: {
				x: this.originPoint.x * scale.width,
				y: this.originPoint.y * scale.height
			}
		};

		var startY = 0;
		var textw,texth;

		//get total height for text---------
		this.textlines = this.text.split("\n");
		item.height = this.textlines.length * item.fontStyle.height;

		item.fontStyle.height *= scale.height;
		this.textH = item.fontStyle.height; 

		ctx.save();
		ctx.translate(item.originPoint.x + canvasCenter.x, item.originPoint.y + canvasCenter.y);

		if (item.rotation !== 0) {
			ctx.rotate(item.rotation * Math.PI / 180);
		}

		if (item.flipped) {
			ctx.scale(-1, 1);
		}

		ctx.font = convertFont(item.fontStyle);

		ctx.textAlign = 'left';  //will need to allow for different alignments
		ctx.textBaseline = 'top';

		startY = -(item.height * scale.height)/2;			//0 = the item centre

		this.textlines.forEach(line => {
			textw = this.measureTextWidth(line, this.fontStyle, this.ctx);
			if (textw > this.width) this.width = textw;

			if (this.fontStyle.shadow) {
				ctx.fillStyle = 'rgba( 0,0,0,.4)';
				ctx.fillText(line, -(this.width * scale.width)/2  + (this.shadowOffset * scale.width), startY + (this.shadowOffset * scale.height));
			} 

			ctx.fillStyle = this.fontStyle.color;
			ctx.fillText(line, -(this.width * scale.width)/2, startY);
			startY += this.textH;
		});

		ctx.restore();
	}

	getCustomImage(size, scale) {

		const textCanvas = document.createElement('canvas');
		const textCanvasContext = textCanvas.getContext('2d');

		textCanvas.width = size.width;
		textCanvas.height = size.height;

		this.preview(scale, textCanvasContext);//draw text on textCanvas. textCanvas dimensions & position relative to parentCanvas centre

		const canvasCenter = {
			x: textCanvas.width / 2,
			y: textCanvas.height / 2,
		};

		const rect = this.getBoundRect();

		const area = {
			x: canvasCenter.x + rect.left * scale.width,
			y: canvasCenter.y + rect.top * scale.height,
			width: (rect.right - rect.left) * scale.width,
			height: (rect.bottom - rect.top) * scale.height
		};

		// crop image to actual size
		const croppedCanvas = document.createElement('canvas');
		const croppedContext = croppedCanvas.getContext('2d');

		croppedCanvas.width = area.width;
		croppedCanvas.height = area.height;

		//draw clipped area of textCanvas to croppedCanvas
		croppedContext.drawImage(textCanvas, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);

		//return cropped canvas as an image.
		return new Promise(resolve => croppedCanvas.toBlob(data => resolve(data)));
	}


	async uploadCustomImage(size, scale) {

		const imageData = this.getCustomImage(size, scale);
		if (imageData) {

			const res = await api.uploadCustomImage(AppSettings.handoverKey, AppSettings.clientId, 1, imageData);

			if (res) {
				console.log('res', res);
				this.imageId = res.Id;
			}
		}

	}

}

export default TextItem;